import { ApiEndpoints } from '@lib/enums/ApiEndpoints';
import { apiUrl } from '@lib/functions/Api';
import { t } from '@lingui/core/macro';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from 'react';

import { getModel3DRenderUrl } from '../components/3d/Model3DViewer';
import {
  IDENTIFY_MODEL_ID,
  embedImages,
  renderModelViewsWithGuard
} from '../components/part/identifyEngine';
import { useApi } from './ApiContext';

/** A part the index builder still needs to process. */
export interface PendingPart {
  pk: number;
  name: string;
  model_3d?: string | null;
  model_3d_preview?: string | null;
  reference_images?: string[];
}

export interface IndexStatus {
  total_with_model: number;
  indexed: number;
  pending: PendingPart[];
}

interface IdentifyIndexValue {
  status: IndexStatus | null;
  indexing: boolean;
  progress: { done: number; total: number };
  error: string | null;
  refreshStatus: () => Promise<void>;
  buildIndex: () => Promise<void>;
  cancel: () => void;
}

const IdentifyIndexContext = createContext<IdentifyIndexValue | null>(null);

/**
 * Global state + driver for the camera-based part-identification index.
 *
 * Lifted out of `IdentifyPartModal` so the indexing loop keeps running when
 * the operator closes the modal: large catalogues take a long time to render
 * + embed in the browser, and trapping the user inside the dialog for the
 * whole run was the main UX complaint. The header shows a progress badge
 * while indexing is active so the operator can monitor without re-opening.
 */
export function IdentifyIndexProvider({
  children
}: Readonly<{ children: ReactNode }>) {
  const api = useApi();
  const [status, setStatus] = useState<IndexStatus | null>(null);
  const [indexing, setIndexing] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [error, setError] = useState<string | null>(null);

  // `cancelRef` is checked between parts so the in-flight loop can break out.
  // `runningRef` makes buildIndex() idempotent — opening the modal a second
  // time while a run is in progress must NOT start a parallel loop.
  const cancelRef = useRef(false);
  const runningRef = useRef(false);

  const refreshStatus = useCallback(async () => {
    try {
      const res = await api.get(apiUrl(ApiEndpoints.part_identify_index), {
        params: { model_id: IDENTIFY_MODEL_ID }
      });
      setStatus(res.data);
    } catch {
      // Non-fatal: the index panel just won't show counts.
      setStatus(null);
    }
  }, [api]);

  const buildIndex = useCallback(async () => {
    if (runningRef.current) return;
    runningRef.current = true;
    setError(null);
    cancelRef.current = false;

    // Always re-fetch the freshest pending list before starting.
    let pending: PendingPart[] = [];
    try {
      const res = await api.get(apiUrl(ApiEndpoints.part_identify_index), {
        params: { model_id: IDENTIFY_MODEL_ID }
      });
      setStatus(res.data);
      pending = res.data?.pending ?? [];
    } catch (err: any) {
      setError(err?.message ?? t`Could not load the index status.`);
      runningRef.current = false;
      return;
    }

    if (pending.length === 0) {
      runningRef.current = false;
      return;
    }

    setIndexing(true);
    setProgress({ done: 0, total: pending.length });
    let ok = 0;
    let failed = 0;
    let firstError = '';
    try {
      for (let i = 0; i < pending.length; i++) {
        if (cancelRef.current) break;
        const part = pending[i];
        try {
          // Build the gallery from rendered 3D views AND reference photos —
          // either or both may be present. A 3D-render failure is caught
          // separately so reference images can still salvage the part.
          const sources: Array<string | Blob> = [];
          const renderUrl = getModel3DRenderUrl(part);
          let renderError: string | null = null;
          if (renderUrl) {
            try {
              sources.push(...(await renderModelViewsWithGuard(renderUrl)));
            } catch (err: any) {
              renderError = err?.message ?? String(err);
            }
          }
          if (part.reference_images?.length) {
            sources.push(...part.reference_images);
          }
          if (sources.length > 0) {
            const vectors = await embedImages(sources);
            await api.post(apiUrl(ApiEndpoints.part_identify_index), {
              part: part.pk,
              embeddings: vectors,
              model_id: IDENTIFY_MODEL_ID,
              n_views: vectors.length
            });
            ok += 1;
          } else if (renderError) {
            // No fallback sources. Persist the render failure so the next
            // index run skips this part — without this, every run would
            // re-attempt the same unrenderable STEP file. The server binds
            // the marker to the current source hash, so replacing the file
            // automatically returns the part to the pending list.
            await api.post(apiUrl(ApiEndpoints.part_identify_index), {
              part: part.pk,
              error: renderError,
              model_id: IDENTIFY_MODEL_ID
            });
            failed += 1;
            if (!firstError) {
              firstError = `${part.name || `#${part.pk}`}: ${renderError}`;
            }
            console.error(
              'Identify index: part',
              part.pk,
              'render failed:',
              renderError
            );
          }
        } catch (err: any) {
          failed += 1;
          if (!firstError) {
            // Prefix with part identity so "First error: …" points at the
            // specific part to inspect, not just what went wrong.
            const detail =
              err?.response?.data?.detail ?? err?.message ?? String(err);
            firstError = `${part.name || `#${part.pk}`}: ${detail}`;
          }
          console.error('Identify index: part', part.pk, 'failed:', err);
        }
        setProgress({ done: i + 1, total: pending.length });
      }
    } finally {
      setIndexing(false);
      runningRef.current = false;
      setError(
        failed > 0
          ? t`Indexed ${ok} part(s); ${failed} failed. First error: ${firstError}`
          : null
      );
      refreshStatus();
    }
  }, [api, refreshStatus]);

  const cancel = useCallback(() => {
    cancelRef.current = true;
  }, []);

  const value = useMemo<IdentifyIndexValue>(
    () => ({
      status,
      indexing,
      progress,
      error,
      refreshStatus,
      buildIndex,
      cancel
    }),
    [status, indexing, progress, error, refreshStatus, buildIndex, cancel]
  );

  return (
    <IdentifyIndexContext.Provider value={value}>
      {children}
    </IdentifyIndexContext.Provider>
  );
}

export function useIdentifyIndex() {
  const ctx = useContext(IdentifyIndexContext);
  if (!ctx) {
    throw new Error(
      'useIdentifyIndex must be used within IdentifyIndexProvider'
    );
  }
  return ctx;
}
